import React, { Component, useState } from "react";
import styled from "styled-components";
import { AutoBind } from "../rule-engine-core/Common/AutoBind";
import { StateManager } from "../rule-engine-core/Common/StateManager";
import { UnitTestConfiguration } from "../rule-engine-core/Common/UnitTesting/UnitTestConfiguration";
import { Sidebar, SidebarItem } from "../CommonUI/BuilderUI/Sidebar";
import { TextMergeBuilderPage } from "../rule-engine-core/TextMerge/TextMergeBuilderPage";
import { RuleTableIcon } from "../CommonUI/Icon/RuleTableIcon";
import { DataBuilder } from "../DataBuilder/DataBuilder";
import { DataIcon } from "../CommonUI/Icon/DataIcon";
import { BuilderEditPage } from "../CommonUI/BuilderUI/BuilderEditPage";
import { CodingIcon } from "../CommonUI/Icon/CodingIcon";
import { XExecutableTester } from "../CommonUI/Testing/XExecutableTester";
import { TestDataIcon } from "../CommonUI/Icon/TestDataIcon";
import { ObserveState } from "../CommonUI/StateManagement/ObserveState";
import { Topbar } from "../CommonUI/BuilderUI/Topbar";
import { TextMergeBuilderInternal } from "./TextMergeBuilderInternal";
import { ExecutionData } from "../rule-engine-core/Common/Data/ExecutionData/ExecutionData";
import { UIHTMLDisplay } from "../CommonUI/BuildlingBlocks/UIHTMLDisplay";
import { Box } from "@mui/system";
import { UIIconButton } from "../CommonUI/BuildlingBlocks/UIIconButton";
import { TextEditorRegistration } from "./Utilities/RegisterTextEditor";
import { UILabel } from "../CommonUI/BuildlingBlocks/UILabel";
import { UIButton } from "../CommonUI/BuildlingBlocks/UIButton";
import { UIStandardLayer } from "../CommonUI/BuildlingBlocks/UIStandardLayer";
import { Grid, TextField } from "@mui/material";
import { UIToggleButtons } from "../CommonUI/BuildlingBlocks/UIToggleButtons";
import { TextMergeConfiguration } from "../rule-engine-core/TextMerge/TextMergeConfiguration";
import { TextMergeEditContext } from "../rule-engine-core/TextMerge/TextMergeEditContext";
import { TextElementRegistration } from "../rule-engine-core/TextMerge/RegisterTextElement";
import { TextSubstitutionEditor } from "./TextSubsitution/TextSubstitutionEditor";
import { SubstitutionTextElement } from "../rule-engine-core/TextMerge/Elements/SubstitutionTextElement";
import { TextMergeType } from "../rule-engine-core/TextMerge/TextMergeType";

export interface ITextMergeBuilder {
    textMergeName?: string;
    onTextMergeNameUpdate?: (newName: string) => void;
    onSave?: (data: TextMergeConfiguration) => void;
    onCancel?: () => void;
    definition: TextMergeConfiguration;
    onChange?: (newValue: TextMergeConfiguration) => void;
    saveUnitTest?: (unitTest: UnitTestConfiguration) => void;
    getUnitTests?: () => Promise<UnitTestConfiguration[]>;
    allowTextTypeSwitch?: boolean;
}

export class TextMergeBuilder extends Component<ITextMergeBuilder, any> {
    tabs: SidebarItem[] = [];
    editContext: TextMergeEditContext;

    constructor(props: ITextMergeBuilder) {
        super(props);
        this.editContext = new TextMergeEditContext(props.definition);

        this.editContext.title = props.textMergeName;

        if (props.onSave) {
            this.editContext.onSave = props.onSave;
        }

        if (props.onCancel) {
            this.editContext.onCancel = props.onCancel;
        }

        this.tabs = this.getDefaultTabs();
    }

    getDefaultTabs(): SidebarItem[] {
        return [
            {
                value: TextMergeBuilderPage.TextEditor,
                renderUI: () => {
                    return (
                        <ObserveState
                            listenTo={this.editContext.definition}
                            properties={["substitution"]}
                            control={() => {
                                if (this.editContext.definition.substitution) {
                                    return (
                                        <TextSubstitutionEditor
                                            config={
                                                (
                                                    this.editContext.definition.rootElement
                                                        .elements[0] as SubstitutionTextElement
                                                ).configuration
                                            }
                                        />
                                    );
                                } else {
                                    return <TextMergeBuilderInternal editContext={this.editContext} />;
                                }
                            }}
                        />
                    );
                },
                icon: <RuleTableIcon />,
            },
            {
                value: TextMergeBuilderPage.Data,
                renderUI: () => (
                    <DataBuilder
                        definition={this.props.definition.getInputDefinition()}
                        readOnly={!this.props.definition.canChangeInputs}
                    />
                ),
                icon: <DataIcon />,
            },
            {
                value: TextMergeBuilderPage.Configuration,
                renderUI: () => (
                    <BuilderEditPage
                        builder={this.props.definition}
                        updateCallback={() => {}}
                    />
                ),
                icon: <CodingIcon />,
            },
            {
                value: TextMergeBuilderPage.Test,
                renderUI: () => (
                    <XExecutableTester
                        saveUnitTest={this.props.saveUnitTest}
                        executable={this.props.definition}
                    />
                ),
                icon: <TestDataIcon />,
            },
        ];
    }

    @AutoBind
    onUpdateName(newName: string) {
        this.editContext.title = newName;
        StateManager.propertyChanged(this.editContext, "title");
        if (this.props.onTextMergeNameUpdate) this.props.onTextMergeNameUpdate(newName);
    }

    render() {
        return (
            <BuilderRender>
                <ObserveState
                    listenTo={this.editContext}
                    properties={["title"]}
                    control={() => {
                        return (
                            <Topbar
                                onChangeName={this.onUpdateName}
                                editContext={this.editContext}
                                name={this.editContext.title}
                                isActionDisabled={false}
                                isRequiredActionGroup={false}
                                afterElems={[
                                    <RenderTopButtons
                                        editContext={this.editContext}
                                        config={this.props.definition}
                                        allowTextTypeSwitch={this.props.allowTextTypeSwitch}
                                    />,
                                ]}
                                // todo: is there a story where save does not show up?
                                onSave={
                                    this.editContext.onSave
                                        ? () => {
                                              if (this.editContext.onSave) {
                                                  this.editContext.onSave(this.editContext.definition);
                                              }
                                          }
                                        : undefined
                                }
                                onClose={this.editContext.onCancel}
                            />
                        );
                    }}
                />
                <ObserveState
                    listenTo={this.editContext}
                    properties={["mode"]}
                    control={() => (
                        <Sidebar
                            items={this.tabs}
                            value={this.editContext.mode}
                            onChange={(mode: string) => {
                                Reflect.set(this.editContext, "mode", mode);
                                StateManager.propertyChanged(this.editContext, "mode");
                            }}
                        />
                    )}
                />
            </BuilderRender>
        );
    }
}

function RenderTopButtons(props: {
    editContext: TextMergeEditContext;
    config: TextMergeConfiguration;
    allowTextTypeSwitch?: boolean;
}): JSX.Element {
    let [showPreview, setShowPreview] = useState<boolean>(false);

    let showSwitch =
        (props.config.type === TextMergeType.HTML ||
            props.config.type === TextMergeType.PlAIN ||
            props.config.type === TextMergeType.MULTI_LINE) &&
        props.allowTextTypeSwitch;

    return (
        <Box
            display={"flex"}
            flexDirection={"row"}
            alignItems={"center"}
        >
            <UILabel
                paddingLeft={"10px"}
                paddingRight={"5px"}
            >
                Type
            </UILabel>

            <Box width={"10px"}></Box>
            <ObserveState
                listenTo={props.editContext.definition}
                control={() => {
                    return (
                        <UIToggleButtons
                            value={props.editContext.definition.substitution === false ? "Builder" : "Substitution"}
                            options={["Builder", "Substitution"]}
                            onChange={(v) => {
                                if (v === "Substitution") props.editContext.definition.substitution = true;
                                else props.editContext.definition.substitution = false;
                            }}
                        ></UIToggleButtons>
                    );
                }}
            />

            {showSwitch && (
                <>
                    <UILabel
                        paddingLeft={"10px"}
                        paddingRight={"5px"}
                    >
                        Display
                    </UILabel>

                    <Box width={"10px"}></Box>
                    <ObserveState
                        listenTo={props.editContext.definition}
                        control={() => {
                            return (
                                <UIToggleButtons
                                    value={props.editContext.definition.type}
                                    options={[TextMergeType.HTML, TextMergeType.PlAIN, TextMergeType.MULTI_LINE]}
                                    onChange={(v) => {
                                        // @ts-ignore
                                        props.editContext.definition.type = v;
                                    }}
                                ></UIToggleButtons>
                            );
                        }}
                    />
                </>
            )}

            <Box width={"10px"}></Box>

            <ObserveState
                listenTo={props.config}
                properties={["substitution", "type"]}
                control={() => {
                    return (
                        <>
                            {props.config.substitution === false && (
                                <>
                                    <UILabel paddingRight={"10px"}>Add</UILabel>
                                    {TextElementRegistration.getAllElements()
                                        .filter((e) => {
                                            return e.example.canAdd(props.config);
                                        })
                                        .map((e) => {
                                            let editor = TextEditorRegistration.getExpressionByTypeName(e.type);
                                            return (
                                                <UIIconButton
                                                    key={e.name}
                                                    paddingRight={"5px"}
                                                    icon={editor?.create(e.example).getIcon()!}
                                                    onClick={() => {
                                                        props.editContext.addElement(e.type);
                                                    }}
                                                ></UIIconButton>
                                            );
                                        })}

                                    <Box width={"10px"}></Box>
                                </>
                            )}
                        </>
                    );
                }}
            />

            <UILabel paddingRight={"10px"}>Default Value</UILabel>
            <ObserveState
                listenTo={props.config}
                properties={["defaultValue"]}
                control={() => {
                    return (
                        <TextField
                            size={"small"}
                            variant={"outlined"}
                            value={props.config.defaultValue}
                            onChange={(e) => {
                                props.config.defaultValue = e.target.value;
                            }}
                        ></TextField>
                    );
                }}
            />

            <Box width={"10px"}></Box>
            <UIButton
                onClick={() => {
                    setShowPreview(true);
                }}
            >
                Preview
            </UIButton>
            <Box width={"10px"}></Box>

            {showPreview && (
                <UIStandardLayer
                    primaryActionText={"Close"}
                    onPrimaryAction={() => setShowPreview(false)}
                    open={showPreview}
                    fullScreen={false}
                    onClose={() => {
                        setShowPreview(false);
                    }}
                    renderUI={() => {
                        return (
                            <Grid
                                minWidth={"500px"}
                                columns={2}
                                container={true}
                            >
                                <Grid
                                    paddingLeft={"10px"}
                                    xs={2}
                                    item={true}
                                >
                                    {props.config.type !== TextMergeType.HTML && (
                                        <code>
                                            <pre>
                                                {props.config.run(
                                                    new ExecutionData({ __Default__Value__Merge__: "[Missing]" }),
                                                    undefined,
                                                )}
                                            </pre>
                                        </code>
                                    )}
                                    {props.config.type === TextMergeType.HTML && (
                                        <UIHTMLDisplay html={props.config.run(new ExecutionData(), undefined)} />
                                    )}
                                </Grid>
                            </Grid>
                        );
                    }}
                    title={"Preview"}
                ></UIStandardLayer>
            )}
        </Box>
    );
}

const BuilderRender = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
`;
